// A Row belongs to a Table, and represents a single level key/value store.
// It provides a wrapper around a raw row object, which is its canonical representation and is always
// kept up-to-date.
import {
  LOCALE_FOR_PRICE,
  KINDS,
  KIND_KEY,
  SHOW_KEY,
} from "@/lib/printess/constants";
import { uuid } from "@/utils/uuid";
import { FULL_CATEGORY_KEY } from "./constants";

export function fuzzyMatch(a, b) {
  const aNormalized = normalizeName(a);
  const bNormalized = normalizeName(b);

  return aNormalized === bNormalized;
}

export function normalizeName(name) {
  if (!name) return "";

  return name
    .replaceAll(/[-_\s]/g, "")
    .trim()
    .toLowerCase();
}

export function formatInt(int) {
  return parseInt(int) || 0;
}

export function formatFloat(float) {
  return parseFloat(float) || 0.0;
}

const defaultPrice = "0,00";
export function formatPrice(price) {
  if (!price) {
    return defaultPrice;
  }

  let newPrice = price;

  if (typeof input === "string" && typeof input.replace === "function") {
    newPrice = newPrice.replace(/,/g, ".");
  }
  newPrice = parseFloat(newPrice);

  if (!newPrice) return defaultPrice;
  // TODO: We probably want this settable via metadata. It dictates the price format.
  // Format the number to 2 decimal places using the user's locale
  newPrice = newPrice.toLocaleString(LOCALE_FOR_PRICE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (!newPrice) return defaultPrice;

  return newPrice;
}

export function formatBoolean(boolean) {
  return !!boolean;
}

export async function fields(api) {
  return JSON.parse(await api.getJson()).formFields;
}

export async function fieldByName(api, name) {
  const foundFields = await fields(api);

  const rawField = foundFields.find((f) => f.data.na === name);

  return rawField;
}

export async function fieldById(api, id, fields) {
  return fields.find((f) => f.id === id);
}

export async function updateField(api, name, value) {
  api.setFormFieldValue(name, value);
}

export function fieldData(field) {
  // dv is probably default value.
  return JSON.parse(field.data.v);
}

export function hasMetaField(items) {
  return !!items.find((item) => item[KIND_KEY] == KINDS.META);
}

export function ensureMetaFieldPresent(items) {
  if (hasMetaField(items)) return;

  items.push({
    name: "meta",
    [KIND_KEY]: KINDS.META,
    [SHOW_KEY]: false,
    key: uuid(),
  });
}

export function matchingCollectionItem(item, collection) {
  return collection.find((collectionItem) => {
    return (
      item.name === collectionItem.name &&
      item[KIND_KEY] === collectionItem[KIND_KEY]
    );
  });
}

export function getItemsFromSourceMenu(sourceMenuData) {
  const grouped_by_category = sourceMenuData.reduce((result, item) => {
    const category = item[FULL_CATEGORY_KEY];
    delete item[FULL_CATEGORY_KEY];

    // Rename 'item-description' to 'description',
    // description is a reserved keyword for OpenAi, so we could not use that in the json schema.
    if ('item-description' in item) {
      item.description = item['item-description'];
      delete item['item-description'];
    }

    result[category] ||= [];
    result[category].push({ ...item, [SHOW_KEY]: false, key: uuid() });

    return result;
  }, {});

  return Object.entries(grouped_by_category).reduce(
    (result, [categoryName, items]) => {
      result.push({
        name: categoryName,
        [KIND_KEY]: KINDS.CATEGORY,
        [SHOW_KEY]: false,
        key: uuid(),
      });

      items.forEach((item) => {
        result.push({
          ...item,
          [KIND_KEY]: KINDS.PRODUCT,
          [SHOW_KEY]: false,
          key: uuid(),
        });
      });

      return result;
    },
    [],
  );
}

export function PaletteItemsFromPrintessAndSourceMenuItems(printessItems, sourceMenuItems) {
  const categoryToIndexes = new Map();
  const paletteItems = [];

  // 1. Reconcile existing printess items with palette
  printessItems.forEach((printessItem, index) => {
    const sourceMenuItem = matchingCollectionItem(printessItem, sourceMenuItems);
    const paletteItem = sourceMenuItem
      ? mergePrintessAndSourceMenuItem(printessItem, sourceMenuItem)
      : createDefaultFromPrintess(printessItem);

    paletteItems.push(paletteItem);

    if (printessItem.category) {
      categoryToIndexes.set(printessItem.category, index);
    }
  });

  // 2. Add missing palette items
  sourceMenuItems.forEach((sourceMenuItem) => {
    if (isItemInPrintess(sourceMenuItem, printessItems)) return;

    const itemToInsert = createDefaultFromSourceMenu(sourceMenuItem);
    const category = sourceMenuItem.category;

    if (category && categoryToIndexes.has(category)) {
      const lastIndex = categoryToIndexes.get(category);
      paletteItems.splice(lastIndex + 1, 0, itemToInsert);
      categoryToIndexes.set(category, lastIndex + 1);
    } else {
      paletteItems.push(itemToInsert);
    }
  });

  return paletteItems;
}

// -- Helpers --

function mergePrintessAndSourceMenuItem(printessItem, paletteItem) {
  return {
    ...paletteItem,
    ...copyPrintessProperties(printessItem),
  };
}

function createDefaultFromPrintess(printessItem) {
  const { _collapsed, ...rest } = printessItem;
  return {
    ...rest,
    [SHOW_KEY]: printessItem[SHOW_KEY] ?? true,
  };
}

function createDefaultFromSourceMenu(sourceMenuItem) {
  return {
    ...sourceMenuItem,
    [SHOW_KEY]: sourceMenuItem[SHOW_KEY] ?? true,
  };
}

function isItemInPrintess(sourceMenuItem, printessItems) {
  return printessItems.some((printessItem) =>
    matchingCollectionItem(printessItem, [sourceMenuItem])
  );
}

function copyPrintessProperties(printessItem) {
  return {
    [SHOW_KEY]: printessItem[SHOW_KEY],
    key: printessItem.key,
    price: printessItem.price,
    description: printessItem.description,
  };
}
